<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable, { DataTableRowClickEvent, DataTableFilterEvent } from 'primevue/datatable';
import Menu from 'primevue/menu';
import MultiSelect from 'primevue/multiselect';
import SplitButton from 'primevue/splitbutton';
import type { MenuItem } from 'primevue/menuitem';
import type { OpportunityBase, OpportunityType } from '@apparatix/types/Leads';
import { useDateFns, useEndpoint, useOpportunities } from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import LeadLabelChip from '@apparatix/leads/LeadLabelChip.vue';
import AccountLabelTag from '@apparatix/leads/AccountLabelTag.vue';
import { useRoute, useRouter } from 'vue-router';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import type { DocumentIdAndName, DocumentIdAndNameResponse } from '@apparatix/types/Documents';

type Props = {
  type: OpportunityType;
  opportunities: OpportunityBase[];
  tableHeight: string;
}

const props = defineProps<Props>();

const selectedLeads = ref<OpportunityBase[]>([]);
const moreMenuRef = ref();

const store = useOpportunityStore();
const { addActivityComponent, showModal } = useCreateActivity();

const {
  buildAddActivity,
  buildAddReminder,
  buildConvertToOpportunity,
  buildMoveToInactiveMenuItem,
  buildProposalItem,
  createProposal,
  getProposalSplitItems,
  openProposal,
  isOpportunityActive,
} = useOpportunities();
const route = useRoute();
const router = useRouter();

const currentOpportunitySummary = ref<OpportunityBase>();

const onRowClick = (e: DataTableRowClickEvent) => {
  store.selectedCampaignId[props.type] = e.data.id;
  const { basePath } = route.meta;
  router.push({ path: `${basePath}/detail/${props.type.toLowerCase()}/${e.data.id}` });
};

const isLoading = ref(false);

const documents = ref<DocumentIdAndName[]>([]);

const docsEndpoint = useEndpoint<DocumentIdAndNameResponse>({
  type: 'GET',
  successField: 'data',
  startInLoadingState: true,
  onSuccess: response => {
    documents.value = response.data.documents;
  },
  toast: { showOnSuccess: false },
});

const { formatDate } = useDateFns();

const moreMenuItems = computed<MenuItem[]>(() => {
  const baseItems: MenuItem[] = [];

  const isActiveStatus = store.options[props.type].possibleStatuses.find(lane => lane.status === currentOpportunitySummary.value?.status)?.state === 'active';
  if (props.type !== 'Lead' && isActiveStatus) {
    baseItems.push(buildProposalItem(documents.value));
  }

  const campaignId = !currentOpportunitySummary.value ? 0 : currentOpportunitySummary.value.id;

  if (isLead()) {
    baseItems.push(buildConvertToOpportunity(campaignId, 'Lead'));
  }

  const objectForActivity = {
    id: campaignId,
    name: !currentOpportunitySummary.value ? '' : currentOpportunitySummary.value.name,
  };

  baseItems.push(
    buildAddReminder(getViewType(), objectForActivity, showModal),
    buildAddActivity(getViewType(), objectForActivity, showModal),
  );

  if (isOpportunityActive(props.type, currentOpportunitySummary.value)) {
    const getCurrentOpportunityId = () => currentOpportunitySummary.value.id;
    baseItems.push(
      buildMoveToInactiveMenuItem(props.type, getCurrentOpportunityId),
    );
  }

  return [
    {
      label: 'MORE',
      items: baseItems,
    },
  ];
});

const isLead = () => props.type === 'Lead';
const getViewType = () => isLead() ? 'leads' : 'opportunities';

const transformedSources = computed(() =>
  store.options[props.type].accountSources.map((src) => ({
    id: src.id,
    source: src.selection,
  })),
);

const accountLabelOptions = computed(() => {
  const labels = props.opportunities
    .map(opportunity => opportunity.accountLabel)
    .filter(label => !!label?.value);

  return labels.filter((label, index, array) => index === array.findIndex(item => item?.id === label.id));
});

const onMoreButtonClick = async (e: MouseEvent, item: OpportunityBase) => {
  isLoading.value = true;
  currentOpportunitySummary.value = item;
  moreMenuRef.value.toggle(e);
  if (currentOpportunitySummary.value && !isLead()) {
    docsEndpoint.setEndpointUrl(`/crm/opportunities/${currentOpportunitySummary.value.id}/active-document-ids`);
    await docsEndpoint.makeRequest();
    isLoading.value = false;
  }
};

const ownerList = computed(() => {
  const owner = props.opportunities?.map(opportunity => opportunity['owner']);
  return [...new Set(owner)];
});

const statusTypes = computed(() => {
  const status = props.opportunities?.map(opportunity => opportunity['status']);
  return [...new Set(status)];
});

const ciTypes = computed(() => {
  const ci = props.opportunities?.map(opportunity => opportunity['ci']).sort((a, b) => a - b);
  const uniqueCi = [...new Set(ci)];

  return uniqueCi.map(ciItem => ({
    label: ciItem.toString() + '%',
    value: parseInt(ciItem, 10),
  }));
});

const officesList = computed(() => {
  const offices = props.opportunities?.map(opportunity => opportunity['office']['office']);
  return [...new Set(offices)];
});

const intervalsList = computed(() => {
  const intervals = props.opportunities?.map(opportunity => opportunity['interval']);
  return [...new Set(intervals)].filter(value => value);
});

const filteredAccountLength = ref<number>();
const onFilterAccounts = (e: DataTableFilterEvent) => {
  filteredAccountLength.value = e.filteredValue.length;
};

const getStatusClass = () => {
  if (props.type === 'Lead') {
    return 'w-3/12';
  }

  return 'w-2/12';
};

const exportCsv = () => {
  const flattened = props.opportunities.map(data => ({
    ...data,
    temperature: data.temperature?.temperature ?? '',
    accountSource: data.accountSource?.source ?? '',
  }));

  dt.value.exportCSV({}, flattened);
};

defineExpose({ exportCsv });

const dt = ref();

watch(
  () => props.type,
  (type) => {
    store.tableRefs[type] = dt;
  },
  { immediate: true },
);

const showFilters = computed(() => store.filters[props.type].isVisible);
</script>
<template>
  <DataTable
    :key="props.type"
    ref="dt"
    v-model:selection="selectedLeads"
    v-model:filters="store.filters[props.type].currentFilters"
    :filter-display="showFilters ? 'row' : null"
    :value="props.opportunities"
    :virtual-scroller-options="{itemSize: 50}"
    data-key="id"
    scrollable
    :scroll-height="props.tableHeight"
    table-layout="fixed"
    reorderable-columns
    resizable-columns
    column-resize-mode="expand"
    @row-click="onRowClick"
    @filter="onFilterAccounts"
  >
    <Column
      header="Id"
      field="id"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('id')"
      :reorderable-column="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Ids"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      header="Name"
      field="name"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('name')"
      :reorderable-column="false"
      class="w-2/12"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Title"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
      <template #body="slotProps">
        <span class="font-semibold">{{ slotProps.data.name }}</span>
      </template>
    </Column>
    <Column
      header="Account"
      field="company"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('company')"
      :reorderable-column="false"
      class="w-2/12"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Accounts"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      header="Account Label"
      field="accountLabel"
      sortable
      :sort-field="'accountLabel.value'"
      :hidden="!store.visibleColumns[props.type].includes('accountLabel')"
      :reorderable-column="false"
      :show-filter-menu="false"
      :exportable="false"
    >
      <template #body="slotProps">
        <AccountLabelTag :label="slotProps.data.accountLabel" />
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="accountLabelOptions"
          filter
          class="p-column-filter"
          option-label="value"
          placeholder="Labels"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      header="Account Market"
      field="accountMarkets"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('accountMarkets')"
      :reorderable-column="false"
    >
      <template #body="slotProps">
        {{ slotProps.data.accountMarkets.join(', ') }}
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Markets"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      header="Primary Contact"
      field="primaryContact"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('primaryContact')"
      :reorderable-column="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Contacts"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="owner"
      sortable
      header="Owner"
      :hidden="!store.visibleColumns[props.type].includes('owner')"
      :reorderable-column="false"
      class="w-2/12"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="ownerList"
          placeholder="Owner"
          filter
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      header="Status"
      field="status"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('status')"
      :reorderable-column="false"
      :class="getStatusClass"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="statusTypes"
          placeholder="Status"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
      <template #body="slotProps">
        <span>{{ slotProps.data.status }}</span>
      </template>
    </Column>
    <template v-if="props.type === 'Opportunity'">
      <Column
        header="Anticipated Start"
        field="anticipatedStart"
        sortable
        :hidden="!store.visibleColumns[props.type].includes('anticipatedStart')"
        :reorderable-column="false"
      >
        <!-- Intentionally pass empty string as Error Message parameter - invalid date expected in some cases -->
        <template #body=" { data }">
          {{ formatDate(data.anticipatedStart, 'MMMM d, uuuu', '') }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-show="showFilters"
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            placeholder="Starts"
            style="min-width: 8rem; max-width: 8rem"
            @input="filterCallback()"
          />
        </template>
      </Column>
      <Column
        header="Due Date"
        field="dueDate"
        sortable
        :hidden="!store.visibleColumns[props.type].includes('dueDate')"
        :reorderable-column="false"
      >
        <!-- Intentionally pass empty string as Error Message parameter - invalid date expected in many cases -->
        <template #body=" { data }">
          {{ formatDate(data.dueDate, 'MMMM d, uuuu', '') }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-show="showFilters"
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            placeholder="Due Dates"
            style="min-width: 8rem; max-width: 8rem"
            @input="filterCallback()"
          />
        </template>
      </Column>
      <Column
        header="Rate"
        field="rateFormatted"
        sortable
        :sort-field="'rate'"
        :hidden="!store.visibleColumns[props.type].includes('rateFormatted')"
        :reorderable-column="false"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-show="showFilters"
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            placeholder="Rates"
            style="min-width: 8rem; max-width: 8rem"
            @input="filterCallback()"
          />
        </template>
      </Column>
      <Column
        header="Interval"
        field="interval"
        sortable
        :hidden="!store.visibleColumns[props.type].includes('interval')"
        :reorderable-column="false"
      >
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-show="showFilters"
            v-model="filterModel.value"
            :options="intervalsList"
            placeholder="Intervals"
            class="p-column-filter"
            style="min-width: 8rem; max-width: 8rem"
            :show-clear="true"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column
        header="Period"
        field="periods"
        sortable
        :hidden="!store.visibleColumns[props.type].includes('periods')"
        :reorderable-column="false"
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-show="showFilters"
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            placeholder="Periods"
            style="min-width: 8rem; max-width: 8rem"
            @input="filterCallback()"
          />
        </template>
      </Column>
    </template>
    <Column
      header="Temperature"
      field="temperature"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('temperature')"
      :reorderable-column="false"
      sort-field="temperature.temperature"
      class="w-1/12"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="store.options[props.type].temperatures"
          option-label="temperature"
          placeholder="Temperature"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
      <template #body="slotProps">
        <LeadLabelChip view="table" :temperature="slotProps.data.temperature" />
      </template>
    </Column>
    <Column
      header="Source"
      field="accountSource"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('accountSource')"
      :reorderable-column="false"
      sort-field="accountSource.source"
    >
      <template #body="slotProps">
        {{ slotProps.data.accountSource.source }}
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="transformedSources"
          option-label="source"
          placeholder="Source"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      v-if="props.type === 'Lead'"
      header="Value"
      field="valueFormatted"
      sortable
      :sort-field="'value'"
      :hidden="!store.visibleColumns[props.type].includes('valueFormatted')"
      :reorderable-column="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Values"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      v-if="props.type === 'Lead'"
      header="Note"
      field="note"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('note')"
      :reorderable-column="false"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Notes"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>

    <Column
      v-if="props.type === 'Opportunity'"
      header="Confidence"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('ci')"
      :reorderable-column="false"
      field="ci"
      class="w-1/12"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="ciTypes"
          option-label="label"
          option-value="value"
          placeholder="CI"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
      <template #body="slotProps">
        <span>{{ slotProps.data.ci }}&percnt;</span>
      </template>
    </Column>
    <Column
      v-if="props.type === 'Opportunity'"
      header="Office"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('office.office')"
      :reorderable-column="false"
      field="office.office"
    >
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-show="showFilters"
          v-model="filterModel.value"
          :options="officesList"
          placeholder="Offices"
          class="p-column-filter"
          style="min-width: 8rem; max-width: 8rem"
          :show-clear="true"
          @change="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="lastUpdatedDate"
      header="Last Updated"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('lastUpdatedDate')"
      :reorderable-column="false"
      :sort-field="'lastUpdatedAt'"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Updates"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <Column
      field="lastActivityDate"
      header="Last Activity"
      sortable
      :hidden="!store.visibleColumns[props.type].includes('lastActivityDate')"
      :reorderable-column="false"
      :sort-field="'lastActivityTimeStamp'"
    >
      <template #filter="{ filterModel, filterCallback }">
        <InputText
          v-show="showFilters"
          v-model="filterModel.value"
          type="text"
          class="p-column-filter"
          placeholder="Activity Date"
          style="min-width: 8rem; max-width: 8rem"
          @input="filterCallback()"
        />
      </template>
    </Column>
    <!--        <Column-->
    <!--          header="Expected close"-->
    <!--          field="expectedClose"-->
    <!--          sortable-->
    <!--          class="w-2/12"-->
    <!--        >-->
    <!--    <template #body="slotProps">-->
    <!--      <span :style="{color: isInThePast(slotProps.data.expectedClose) ? '#FF385C' : '#5A7090'}">{{ formatDate(slotProps.data.expectedClose, 'MMMM d, uuuu', 'Unknown') }}</span>-->
    <!--    </template>-->
    <!--    </Column>-->
    <Column
      :reorderable-column="false"
      frozen
      align-frozen="right"
    >
      <template #body="slotProps">
        <Button
          icon="pi pi-ellipsis-h"
          severity="secondary"
          outlined
          :pt="{
            root: {
              style: {
                padding: '4px',
                width: '35px',
                height: '20px',
                fontSize: '.5rem',
                border: 'none'
              }
            },
            icon: {
              style: {
                fontSize: '1.3rem'
              }
            }
          }"
          @click="e => onMoreButtonClick(e, slotProps.data)"
        />
        <Menu
          ref="moreMenuRef"
          :model="moreMenuItems"
          :popup="true"
        >
          <template #item="{ item, props: menuSlotProps }">
            <div v-if="item.id === 'openProposal'" @click.stop>
              <FormSkeleton v-if="isLoading" :rows="1" :compact="true" />
              <SplitButton
                v-else
                label="Open Proposal"
                icon="pi pi-pen-to-square"
                :model="getProposalSplitItems(documents ?? [], currentOpportunitySummary)"
                button-class="text-primary bg-primary/10 hover:bg-primary/20"
                menu-button-class="text-primary bg-primary/10 hover:bg-primary/20"
                append-to="body"
                @click="openProposal(documents[0].id, currentOpportunitySummary)"
                @dropdown-click.stop
              />
            </div>
            <div v-else-if="item.id === 'createProposal'">
              <FormSkeleton v-if="isLoading" :rows="1" :compact="true" />
              <Button
                v-else
                label="Create Proposal"
                icon="pi pi-pen-to-square"
                button-class="text-primary bg-primary/10 hover:bg-primary/20"
                class="w-full"
                @click="createProposal(currentOpportunitySummary)"
              />
            </div>

            <!-- 2) Fall back to the default link layout for all others -->
            <template v-else>
              <a
                v-ripple
                class="flex items-center gap-2 px-3 py-2 hover:bg-surface-200 rounded"
                v-bind="menuSlotProps.action"
              >
                <span :class="item.icon" />
                <span>{{ item.label }}</span>
              </a>
            </template>
          </template>
        </Menu>
      </template>
    </Column>
  </DataTable>
  <component :is="addActivityComponent" />
</template>
